import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { DataTable } from '@repo/ui/commons/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/commons/data-table/types'
import {
	getAdminCatalogNeedsCatalog,
	type GetAdminCatalogNeedsCatalogResponse
} from '@repo/sdk/gen'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { RefreshCw } from 'lucide-react'
import { z } from 'zod'
import { BackofficeShell } from '@/components/backoffice-shell'
import { CatalogThumbnail } from '@/components/catalog-thumbnail'
import { QueryErrorState } from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'
import { DebouncedSearchInput } from '../-components/debounced-search-input'
import { ResetFiltersButton } from '../-components/reset-filters-button'

export const Route = createFileRoute('/queue/')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	validateSearch: z.object({
		limit: z.coerce.number().int().min(10).max(100).optional(),
		offset: z.coerce.number().int().nonnegative().optional(),
		q: z.string().max(64).optional()
	}),
	component: CatalogQueue
})

function CatalogQueue() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const search = Route.useSearch()
	const limit = search.limit ?? 50
	const offset = search.offset ?? 0
	const queueQuery = useQuery({
		queryKey: ['catalog', 'needs-catalog', search],
		queryFn: () =>
			getAdminCatalogNeedsCatalog({
				query: { limit, offset, q: search.q }
			}).then((response) => response.data)
	})
	const data = queueQuery.data?.data ?? []
	const hasActiveFilters = Boolean(search.q)
	const columns: DataTableColumn<
		GetAdminCatalogNeedsCatalogResponse['data'][number]
	>[] = [
		{
			id: 'image',
			header: t('common:image'),
			cell: (item) => <CatalogThumbnail src={item.imageUrl} />,
			hideable: false,
			width: 76
		},
		{
			id: 'product',
			header: t('common:provider_product'),
			cell: (item) => (
				<div className='min-w-0'>
					<p className='truncate font-medium'>
						{item.name ?? item.providerProductId}
					</p>
					<p className='truncate text-muted-foreground text-xs'>
						{item.provider} · {item.providerProductId}
					</p>
				</div>
			),
			flex: 1,
			minWidth: 260
		},
		{
			id: 'catalogReference',
			header: t('common:catalog_reference'),
			cell: (item) => (
				<div>
					<p className='font-medium'>{item.cardNumber ?? '—'}</p>
					<p className='text-muted-foreground text-xs'>
						{item.episodeCode ?? '—'}
					</p>
				</div>
			),
			width: 180
		},
		{
			id: 'providerVersion',
			header: t('common:provider_version'),
			cell: (item) => (
				<Badge variant='secondary'>{item.providerVersion ?? '—'}</Badge>
			),
			width: 180
		},
		{
			id: 'actions',
			header: t('common:actions'),
			cell: (item) => (
				<div className='flex justify-end'>
					<Button
						render={
							<Link params={{ id: item.id }} to='/queue/$id' />
						}
						size='sm'
					>
						{t('common:review_match')}
					</Button>
				</div>
			),
			pinned: 'right',
			width: 180,
			minWidth: 180,
			maxWidth: 180,
			enableResizing: false
		}
	]

	function updateSearch(next: Partial<typeof search>) {
		navigate({ to: '/queue', search: { ...search, ...next } })
	}

	return (
		<BackofficeShell contentClassName='max-w-none'>
			<header className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
				<div>
					<p className='font-medium text-primary text-sm'>
						{t('common:reconciliation')}
					</p>
					<h1 className='mt-2 font-semibold text-3xl tracking-tight'>
						{t('common:needs_catalog')}
					</h1>
					<p className='mt-2 text-muted-foreground text-sm'>
						{t('common:queue_copy', {
							count: queueQuery.data?.meta.total ?? 0
						})}
					</p>
				</div>
				<Button
					disabled={queueQuery.isFetching}
					onClick={() => queueQuery.refetch()}
					variant='outline'
				>
					<RefreshCw
						className={queueQuery.isFetching ? 'animate-spin' : ''}
					/>
					{t('common:refresh')}
				</Button>
			</header>
			<div className='mt-6 flex flex-wrap gap-3'>
				<DebouncedSearchInput
					ariaLabel={t('common:search_queue')}
					className='w-full sm:w-80'
					onValueChange={(q) =>
						updateSearch({ offset: 0, q: q || undefined })
					}
					placeholder={t('common:search_queue')}
					value={search.q ?? ''}
				/>
				<ResetFiltersButton
					disabled={!hasActiveFilters}
					onClick={() => updateSearch({ offset: 0, q: undefined })}
				/>
			</div>
			<DataTable
				className='mt-6 h-[calc(100vh-19rem)] min-h-96'
				columns={columns}
				data={data}
				pagination={{
					onPageChange: (pageIndex) =>
						updateSearch({ offset: pageIndex * limit }),
					onPageSizeChange: (nextLimit) =>
						updateSearch({ limit: nextLimit, offset: 0 }),
					pageCount: Math.ceil(
						(queueQuery.data?.meta.total ?? 0) / limit
					),
					pageIndex: Math.floor(offset / limit),
					pageSize: limit
				}}
				state={{
					empty: t('common:empty_queue'),
					error: queueQuery.isError ? (
						<QueryErrorState
							message={t('common:queue_load_error')}
							onRetry={() => queueQuery.refetch()}
						/>
					) : (
						false
					),
					loading: queueQuery.isPending
				}}
			/>
		</BackofficeShell>
	)
}
